/**
 * Service de génération de la lettre de convocation d'Assemblée Générale
 *
 * Utilise docxtemplater pour générer un document Word à partir d'un template
 * conforme aux exigences légales (Article 9 du décret du 17 mars 1967)
 */

import Docxtemplater from 'docxtemplater'
import PizZip from 'pizzip'
import { saveAs } from 'file-saver'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import type { Meeting, MeetingType, Resolution } from '../types'

// ===== TYPES =====

export interface ConvocationOwnerData {
  id: string
  first_name: string
  last_name: string
  civility?: 'M' | 'MME' | null
  address_line1?: string | null
  address_line2?: string | null
  postal_code?: string | null
  city?: string | null
  email?: string | null
}

export interface ConvocationUnitData {
  id: string
  unit_number: string
}

export interface ConvocationAgencyData {
  name: string
  raison_sociale?: string | null
  address?: string | null
  telephone?: string | null
  email?: string | null
  forme_juridique?: string | null
  capital?: number | null
  siret?: string | null
  rcs_ville?: string | null
  carte_pro?: string | null
  carte_pro_delivree_par?: string | null
  garantie_financiere?: string | null
  garantie_adresse?: string | null
  orias?: string | null
}

export interface ConvocationResidenceData {
  id: string
  name: string
  address?: string | null
  city?: string | null
  postal_code?: string | null
}

export interface ConvocationGestionnaireData {
  nom: string
  email?: string | null
}

export interface ConvocationDirecteurData {
  nom: string
}

export interface ConvocationResolutionData {
  order_number: number
  title: string
  description: string
  majority_label: string
}

export interface ConvocationGenerationParams {
  meeting: Meeting
  owner: ConvocationOwnerData
  units: ConvocationUnitData[]
  agency: ConvocationAgencyData
  residence: ConvocationResidenceData
  gestionnaire: ConvocationGestionnaireData
  directeur?: ConvocationDirecteurData
  resolutions?: Resolution[]
  lieuEmission?: string
}

export interface ConvocationFormData {
  // En-tête
  lieu_emission: string
  date_emission: string

  // Agence (expéditeur)
  agence_nom: string
  copropriete_code: string
  coproprietaire_reference: string

  // Destinataire
  destinataire_civilite: string
  destinataire_nom: string
  destinataire_adresse_ligne1: string
  destinataire_adresse_ligne2: string
  destinataire_adresse_cp: string
  destinataire_adresse_ville: string

  // AG
  ag_type_label: string
  ag_date: string
  ag_heure: string
  ag_lieu: string
  ag_visio_activee: boolean
  ag_visio_url: string

  // Copropriété
  copropriete_nom: string
  copropriete_adresse_complete: string

  // Signataires
  gestionnaire_nom: string
  gestionnaire_email: string
  directeur_nom: string

  // Pied de page - Mentions légales agence
  agence_raison_sociale: string
  agence_telephone: string
  agence_adresse: string
  agence_forme_juridique: string
  agence_capital: string
  agence_siret: string
  agence_rcs_ville: string
  agence_carte_pro: string
  agence_carte_pro_delivree_par: string
  agence_garantie_financiere: string
  agence_garantie_adresse: string
  agence_orias: string

  // Résolutions (ordre du jour)
  resolutions: ConvocationResolutionData[]
  resolutions_count: number
}

// ===== HELPER FUNCTIONS =====

/**
 * Génère le code de référence copropriétaire
 * Format : [RESIDENCE_KEY]-[OWNER_INITIALS]-[ID_SHORT]
 */
export function generateOwnerReference(
  owner: ConvocationOwnerData,
  residence: ConvocationResidenceData
): string {
  const residenceKey = residence.id.slice(0, 4).toUpperCase()
  const initials = `${owner.first_name.charAt(0)}${owner.last_name.charAt(0)}`.toUpperCase()
  const idShort = owner.id.slice(0, 6).toUpperCase()
  return `${residenceKey}-${initials}-${idShort}`
}

/**
 * Formate la civilité
 */
function formatCivility(civility: 'M' | 'MME' | null | undefined): string {
  switch (civility) {
    case 'M':
      return 'Monsieur'
    case 'MME':
      return 'Madame'
    default:
      return 'Madame, Monsieur'
  }
}

/**
 * Formate l'adresse complète de la résidence
 */
function formatResidenceAddress(residence: ConvocationResidenceData): string {
  const parts = []
  if (residence.address) parts.push(residence.address)
  if (residence.postal_code && residence.city) {
    parts.push(`${residence.postal_code} ${residence.city}`)
  }
  return parts.join(', ')
}

/**
 * Retourne le label du type d'AG
 */
function getMeetingTypeLabel(type: MeetingType): string {
  const labels: Record<MeetingType, string> = {
    ordinary: 'ordinaire',
    extraordinary: 'extraordinaire',
    mixed: 'mixte',
  }
  return labels[type] || ''
}

/**
 * Retourne le label court de la majorité
 */
function getMajorityLabel(majorityType: string | null): string {
  const labels: Record<string, string> = {
    simple: 'Article 24',
    absolute: 'Article 25',
    absolute_25_1: 'Article 25-1',
    double: 'Article 26',
    unanimity: 'Unanimité',
  }
  return labels[majorityType || 'simple'] || 'Article 24'
}

/**
 * Prépare les résolutions pour le template
 */
function prepareResolutions(resolutions?: Resolution[]): ConvocationResolutionData[] {
  if (!resolutions || resolutions.length === 0) {
    return []
  }

  return resolutions
    .filter((r) => r.status !== 'DRAFT') // Ne pas inclure les brouillons
    .sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0))
    .map((r) => ({
      order_number: r.order_number ?? 0,
      title: r.title,
      description: r.description || '',
      majority_label: getMajorityLabel(r.majority_type),
    }))
}

/**
 * Prépare les données pour le template de convocation
 */
export function prepareConvocationFormData(params: ConvocationGenerationParams): ConvocationFormData {
  const { meeting, owner, agency, residence, gestionnaire, directeur, resolutions, lieuEmission } = params

  const meetingDate = new Date(meeting.meeting_date)

  // Formater l'heure (HH:MM)
  const startTime = meeting.start_time || '14:00'
  const formattedTime = startTime.length > 5 ? startTime.substring(0, 5) : startTime

  return {
    // En-tête
    lieu_emission: lieuEmission || residence.city || 'Paris',
    date_emission: format(new Date(), 'd MMMM yyyy', { locale: fr }),

    // Agence (expéditeur)
    agence_nom: agency.name,
    copropriete_code: residence.id.slice(0, 4).toUpperCase(),
    coproprietaire_reference: generateOwnerReference(owner, residence),

    // Destinataire
    destinataire_civilite: formatCivility(owner.civility),
    destinataire_nom: `${owner.last_name.toUpperCase()} ${owner.first_name}`,
    destinataire_adresse_ligne1: owner.address_line1?.toUpperCase() || '',
    destinataire_adresse_ligne2: owner.address_line2 || '',
    destinataire_adresse_cp: owner.postal_code || '',
    destinataire_adresse_ville: owner.city?.toUpperCase() || '',

    // AG
    ag_type_label: getMeetingTypeLabel(meeting.meeting_type as MeetingType),
    ag_date: format(meetingDate, 'd MMMM yyyy', { locale: fr }),
    ag_heure: formattedTime,
    ag_lieu: meeting.location || 'À préciser',
    ag_visio_activee: 'video_enabled' in meeting && meeting.video_enabled === true,
    ag_visio_url: ('video_room_url' in meeting && meeting.video_room_url) || '',

    // Copropriété
    copropriete_nom: residence.name.toUpperCase(),
    copropriete_adresse_complete: formatResidenceAddress(residence),

    // Signataires
    gestionnaire_nom: gestionnaire.nom,
    gestionnaire_email: gestionnaire.email || '',
    directeur_nom: directeur?.nom || '',

    // Pied de page - Mentions légales agence
    agence_raison_sociale: agency.raison_sociale || agency.name,
    agence_telephone: agency.telephone || '',
    agence_adresse: agency.address || '',
    agence_forme_juridique: agency.forme_juridique || '',
    agence_capital: agency.capital ? agency.capital.toFixed(2) : '',
    agence_siret: agency.siret || '',
    agence_rcs_ville: agency.rcs_ville || '',
    agence_carte_pro: agency.carte_pro || '',
    agence_carte_pro_delivree_par: agency.carte_pro_delivree_par || '',
    agence_garantie_financiere: agency.garantie_financiere || '',
    agence_garantie_adresse: agency.garantie_adresse || '',
    agence_orias: agency.orias || '',

    // Résolutions (ordre du jour)
    resolutions: prepareResolutions(resolutions),
    resolutions_count: resolutions ? resolutions.filter((r) => r.status !== 'DRAFT').length : 0,
  }
}

// ===== MAIN GENERATION FUNCTION =====

/**
 * Génère la lettre de convocation et retourne un Blob
 */
export async function generateConvocationLetter(params: ConvocationGenerationParams): Promise<Blob> {
  // Charger le template
  const templateUrl = '/templates/convocation-template.docx'
  const response = await fetch(templateUrl)

  if (!response.ok) {
    throw new Error(`Template non trouvé: ${templateUrl}`)
  }

  const templateArrayBuffer = await response.arrayBuffer()

  // Créer le document avec PizZip
  const zip = new PizZip(templateArrayBuffer)
  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    // Options pour éviter les erreurs sur les tags non trouvés
    nullGetter: () => '',
  })

  // Préparer les données
  const data = prepareConvocationFormData(params)

  // Remplir le template
  doc.render(data)

  // Générer le blob
  const blob = doc.getZip().generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  })

  return blob
}

/**
 * Génère et télécharge la lettre de convocation
 */
export async function downloadConvocationLetter(params: ConvocationGenerationParams): Promise<void> {
  const blob = await generateConvocationLetter(params)

  // Générer le nom du fichier
  const meetingDate = new Date(params.meeting.meeting_date)
  const dateStr = format(meetingDate, 'yyyyMMdd')
  const ownerName = `${params.owner.last_name}_${params.owner.first_name}`.replace(/\s+/g, '_')
  const fileName = `CONVOC_${dateStr}_${ownerName}.docx`

  // Télécharger
  saveAs(blob, fileName)
}
